import { useEffect, useMemo, useState } from "react";import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ComposeEmailModal from "@/components/admin/ComposeEmailModal";
import {
  Phone,
  Mail,
  Search,
  LogOut,
  Loader2,
  Users,
  Inbox,
  CalendarClock,
  Heart,
  CheckCircle2,
  Clock,
  PhoneCall,
  ExternalLink,
  Filter,
  StickyNote,
  Save,
  Briefcase,
  FileText,
  Download,
  Send,
  DollarSign,
  Plus,
  X,
  Trash2,
  Pencil,
  Percent,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ORDER = ["pending", "contacted", "done"];
const STATUS_META = {
  pending: {
    label: "Pending",
    icon: Clock,
    className:
      "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
    dot: "bg-amber-500",
  },
  contacted: {
    label: "Contacted",
    icon: PhoneCall,
    className:
      "bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-200",
    dot: "bg-blue-500",
  },
  done: {
    label: "Done",
    icon: CheckCircle2,
    className:
      "bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200",
    dot: "bg-emerald-500",
  },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(
    location.state?.user ? "authed" : "checking"
  );
  const [authError, setAuthError] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updating, setUpdating] = useState({}); // { [id]: true }

  // 1. Verify session with backend (unless AuthCallback already passed the user)
  useEffect(() => {
    if (authState === "authed") return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 10000,
        });
        setUser(res.data);
        setAuthState("authed");
      } catch (err) {
        // 403 = not allowlisted — show error, don't loop back to login
        if (err?.response?.status === 403) {
          setAuthError(
            err?.response?.data?.detail ||
              "Your account is not authorised to access this dashboard."
          );
          setAuthState("forbidden");
          return;
        }
        setAuthState("unauthed");
        navigate("/admin/login", { replace: true });
      }
    })();
  }, [authState, navigate]);

  // 2. Load enquiries when authed
  useEffect(() => {
    if (authState !== "authed") return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/enquiries?limit=500`, {
          withCredentials: true,
          timeout: 15000,
        });
        setEnquiries(res.data);
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          "Couldn't load enquiries. Please refresh.";
        toast.error(typeof detail === "string" ? detail : "Load failed.");
      } finally {
        setLoading(false);
      }
    })();
  }, [authState]);

  const handleLogout = async () => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.warn("Logout request failed (session may already be invalid):", err);
    }
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, newStatus) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    try {
      const res = await axios.patch(
        `${API}/enquiries/${id}`,
        { status: newStatus },
        { withCredentials: true, timeout: 10000 }
      );
      setEnquiries((list) =>
        list.map((e) => (e.id === id ? { ...e, status: res.data.status } : e))
      );
      toast.success(`Marked as ${STATUS_META[newStatus].label}`);
    } catch (err) {
      console.error("Failed to update enquiry status:", err);
      toast.error("Couldn't update status. Please try again.");
    } finally {
      setUpdating((u) => {
        const next = { ...u };
        delete next[id];
        return next;
      });
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      const res = await axios.patch(
        `${API}/enquiries/${id}`,
        { notes },
        { withCredentials: true, timeout: 10000 }
      );
      setEnquiries((list) =>
        list.map((e) => (e.id === id ? { ...e, notes: res.data.notes } : e))
      );
      toast.success("Notes saved");
      return true;
    } catch (err) {
      console.error("Failed to save enquiry notes:", err);
      toast.error("Couldn't save notes. Please try again.");
      return false;
    }
  };

  const deleteEnquiry = async (enquiry) => {
    // Optimistic remove + 5s undo window. The DELETE only fires once the toast
    // times out — clicking Undo cancels it and restores the card in place.
    const idx = enquiries.findIndex((e) => e.id === enquiry.id);
    if (idx === -1) return;

    setEnquiries((list) => list.filter((e) => e.id !== enquiry.id));

    let undone = false;
    const timer = setTimeout(async () => {
      if (undone) return;
      try {
        const res = await axios.delete(`${API}/enquiries/${enquiry.id}`, {
          withCredentials: true,
          timeout: 15000,
        });
        const inv = res.data?.invoices_deleted || 0;
        if (inv > 0) {
          toast.success(`Also removed ${inv} linked invoice${inv === 1 ? "" : "s"}.`);
        }
      } catch (err) {
        console.error("Failed to delete enquiry:", err);
        const detail = err?.response?.data?.detail || "Couldn't delete enquiry.";
        toast.error(typeof detail === "string" ? detail : "Delete failed.");
        // Put it back at (roughly) its original position
        setEnquiries((list) => {
          if (list.some((e) => e.id === enquiry.id)) return list;
          const next = [...list];
          next.splice(Math.min(idx, next.length), 0, enquiry);
          return next;
        });
      }
    }, 5000);

    toast(`Deleted enquiry from ${enquiry.name}`, {
      description: "Also removes any linked invoices in 5 seconds.",
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          setEnquiries((list) => {
            if (list.some((e) => e.id === enquiry.id)) return list;
            const next = [...list];
            next.splice(Math.min(idx, next.length), 0, enquiry);
            return next;
          });
          toast.success("Restored");
        },
      },
    });
  };

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      total: enquiries.length,
      thisWeek: enquiries.filter((e) => new Date(e.created_at) >= weekAgo)
        .length,
      seniors: enquiries.filter((e) => e.is_senior).length,
      business: enquiries.filter((e) => e.is_business).length,
      pending: enquiries.filter(
        (e) => (e.status || "pending") === "pending"
      ).length,
    };
  }, [enquiries]);

  // Filtered list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enquiries.filter((e) => {
      const st = e.status || "pending";
      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (!q) return true;
      return (
        (e.name || "").toLowerCase().includes(q) ||
        (e.phone || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.service || "").toLowerCase().includes(q) ||
        (e.message || "").toLowerCase().includes(q)
      );
    });
  }, [enquiries, search, statusFilter]);

  if (authState === "checking") {
    return (
      <main
        data-testid="admin-dashboard-loading"
        className="min-h-screen flex items-center justify-center bg-slate-50"
      >
        <div className="flex items-center gap-3 text-slate-700">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-lg">Loading admin dashboard...</span>
        </div>
      </main>
    );
  }

  if (authState === "forbidden") {
    return (
      <main
        data-testid="admin-forbidden-page"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
      >
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-rose-100 flex items-center justify-center mx-auto mb-5">
            <LogOut className="w-7 h-7 text-rose-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Not authorised</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">{authError}</p>
          <button
            onClick={handleLogout}
            data-testid="admin-forbidden-signout-button"
            className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold"
          >
            Sign out and try a different account
          </button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard-page" className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-2xl font-bold tracking-tight text-slate-900 hover:text-[#1E56B8]"
            >
              DazTec <span className="text-[#1E56B8]">Admin</span>
            </Link>
            <nav className="hidden sm:flex items-center gap-1 ml-2">
              <Link
                to="/admin"
                data-testid="admin-nav-enquiries"
                className="h-8 px-3 rounded-md text-sm font-medium bg-slate-100 text-slate-900"
              >
                Enquiries
              </Link>
              <Link
                to="/admin/invoices"
                data-testid="admin-nav-invoices"
                className="h-8 px-3 rounded-md text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 inline-flex items-center gap-1"
              >
                <FileText className="w-3.5 h-3.5" />
                Invoices
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div
                data-testid="admin-user-info"
                className="hidden sm:flex items-center gap-3 text-sm"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border border-slate-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#1E56B8] text-white flex items-center justify-center font-semibold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="leading-tight">
                  <div className="font-medium text-slate-900">{user.name}</div>
                  <div className="text-slate-500 text-xs">{user.email}</div>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard
            testId="stat-total"
            icon={Inbox}
            label="Total enquiries"
            value={stats.total}
            tone="slate"
          />
          <StatCard
            testId="stat-this-week"
            icon={CalendarClock}
            label="This week"
            value={stats.thisWeek}
            tone="blue"
          />
          <StatCard
            testId="stat-pending"
            icon={Clock}
            label="Pending"
            value={stats.pending}
            tone="amber"
          />
          <StatCard
            testId="stat-seniors"
            icon={Heart}
            label="Seniors"
            value={stats.seniors}
            tone="rose"
          />
          <StatCard
            testId="stat-business"
            icon={Briefcase}
            label="Business"
            value={stats.business}
            tone="indigo"
          />
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, service, or message..."
              data-testid="admin-search-input"
              className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-300 bg-white text-base text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
            />
          </div>
          <div className="relative">
            <Filter className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-status-filter"
              className="h-12 pl-12 pr-8 rounded-xl border border-slate-300 bg-white text-base text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none appearance-none"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="done">Done</option>
            </select>
          </div>
        </div>

        {/* Enquiries list */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-600">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            Loading enquiries...
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="text-center py-24 bg-white border border-slate-200 rounded-2xl"
          >
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900">
              {enquiries.length === 0
                ? "No enquiries yet"
                : "No matches for your search"}
            </h3>
            <p className="mt-2 text-slate-600">
              {enquiries.length === 0
                ? "Submissions will appear here as they come in."
                : "Try clearing the search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {filtered.map((e) => (
              <EnquiryCard
                key={e.id}
                enquiry={e}
                onUpdateStatus={updateStatus}
                onSaveNotes={saveNotes}
                onDelete={deleteEnquiry}
                updating={!!updating[e.id]}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export function StatCard({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm text-slate-500 font-medium">{label}</div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );
}

function EnquiryCard({ enquiry, onUpdateStatus, onSaveNotes, onDelete, updating }) {
  const status = enquiry.status || "pending";
  const meta = STATUS_META[status];
  const dt = new Date(enquiry.created_at);
  const dateLabel = dt.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const [notesDraft, setNotesDraft] = useState(enquiry.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => {
    setNotesDraft(enquiry.notes || "");
  }, [enquiry.notes]);

  const notesDirty = (notesDraft || "") !== (enquiry.notes || "");

  const handleSaveNotes = async () => {
    if (!notesDirty || savingNotes) return;
    setSavingNotes(true);
    await onSaveNotes(enquiry.id, notesDraft);
    setSavingNotes(false);
  };

  return (
    <article
      data-testid={`enquiry-card-${enquiry.id}`}
      className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-bold text-slate-900">{enquiry.name}</h3>
            {enquiry.is_senior && (
              <span
                data-testid={`senior-badge-${enquiry.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-rose-100 text-rose-800"
              >
                <Heart className="w-3 h-3" />
                Senior — free
              </span>
            )}
            {enquiry.is_business && (
              <span
                data-testid={`business-badge-${enquiry.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-800"
              >
                <Briefcase className="w-3 h-3" />
                Business — $2/min
              </span>
            )}
            <span
              data-testid={`status-badge-${enquiry.id}`}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${meta.className}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {enquiry.service} · {dateLabel}
          </p>
        </div>

        {/* Contact quick actions */}
        <div className="flex gap-2 shrink-0">
          <a
            href={`tel:${enquiry.phone}`}
            data-testid={`call-button-${enquiry.id}`}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold"
          >
            <Phone className="w-4 h-4" />
            {enquiry.phone}
          </a>
          {enquiry.email && (
            <a
              href={`mailto:${enquiry.email}?subject=${encodeURIComponent(
                "Re: your DazTec enquiry"
              )}`}
              data-testid={`email-button-${enquiry.id}`}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-semibold"
            >
              <Mail className="w-4 h-4" />
              Email
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      {/* Message */}
      <div className="bg-slate-50 border-l-4 border-[#1E56B8] rounded-r-lg px-4 py-3 mb-4">
        <p className="text-slate-800 whitespace-pre-wrap">{enquiry.message}</p>
      </div>

      {/* Status change buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mr-1">
          Mark as:
        </span>
        {STATUS_ORDER.map((s) => {
          const StatIcon = STATUS_META[s].icon;
          const active = status === s;
          return (
            <button
              key={s}
              disabled={updating || active}
              onClick={() => onUpdateStatus(enquiry.id, s)}
              data-testid={`status-${s}-button-${enquiry.id}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-slate-900 text-white cursor-default"
                  : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
              } ${updating ? "opacity-50 cursor-wait" : ""}`}
            >
              {updating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <StatIcon className="w-4 h-4" />
              )}
              {STATUS_META[s].label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onDelete(enquiry)}
          disabled={updating}
          data-testid={`delete-enquiry-button-${enquiry.id}`}
          title="Delete this enquiry (and any linked invoices)"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>

      {/* Private notes */}
      <div className="mt-5 pt-5 border-t border-slate-200">
        <label
          htmlFor={`notes-${enquiry.id}`}
          className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2"
        >
          <StickyNote className="w-4 h-4" />
          Private notes (only you see these)
        </label>
        <textarea
          id={`notes-${enquiry.id}`}
          data-testid={`notes-textarea-${enquiry.id}`}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="e.g. Called Tuesday 2pm — left voicemail. Booking site visit Friday."
          rows={2}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1E56B8] focus:ring-0 outline-none resize-y"
        />
        {notesDirty && (
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              onClick={() => setNotesDraft(enquiry.notes || "")}
              data-testid={`notes-cancel-${enquiry.id}`}
              className="h-8 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              data-testid={`notes-save-${enquiry.id}`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold disabled:opacity-60"
            >
              {savingNotes ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save notes
            </button>
          </div>
        )}
      </div>

      {/* Invoicing */}
      <InvoicesPanel enquiry={enquiry} />
    </article>
  );
}


const INVOICE_STATUS_META = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-700 ring-1 ring-slate-200" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-800 ring-1 ring-blue-200" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200" },
};

function defaultRateFor(enquiry) {
  if (enquiry.is_business) return 2;
  if (enquiry.is_senior) return 0;
  return 1;
}

function defaultRateLabelFor(enquiry) {
  if (enquiry.is_business) return "$2 business";
  if (enquiry.is_senior) return "$0 senior";
  return "$1 home";
}

const genLineKey = () => `k_${Math.random().toString(36).slice(2)}`;
const withLineKeys = (lines) =>
  (lines || []).map((l) => ({ ...l, _key: l._key || genLineKey() }));

function InvoicesPanel({ enquiry }) {
  const [invoices, setInvoices] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [rate, setRate] = useState(defaultRateFor(enquiry));
  const [description, setDescription] = useState(enquiry.service || "");
  const [busyId, setBusyId] = useState(null);
  const [composeInv, setComposeInv] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [invRes, presetRes] = await Promise.all([
          axios.get(`${API}/invoices?enquiry_id=${enquiry.id}`, {
            withCredentials: true,
            timeout: 10000,
          }),
          axios.get(`${API}/invoice-presets`, {
            withCredentials: true,
            timeout: 10000,
          }),
        ]);
        if (alive) {
          setInvoices(invRes.data);
          setPresets(presetRes.data);
        }
      } catch (err) {
        console.error("Load invoices failed:", err);
        if (alive) toast.error("Couldn't load invoices.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [enquiry.id]);

  const total = useMemo(() => {
    const m = parseFloat(minutes);
    const r = parseFloat(rate);
    if (Number.isFinite(m) && Number.isFinite(r)) return (m * r).toFixed(2);
    return "0.00";
  }, [minutes, rate]);

  const resetForm = () => {
    setMinutes("");
    setRate(defaultRateFor(enquiry));
    setDescription(enquiry.service || "");
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const m = parseFloat(minutes);
    const r = parseFloat(rate);
    if (!Number.isFinite(m) || m <= 0) {
      toast.error("Enter the minutes worked.");
      return;
    }
    if (!Number.isFinite(r) || r < 0) {
      toast.error("Enter a valid rate.");
      return;
    }
    if (!description.trim()) {
      toast.error("Add a short service description.");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/invoices`,
        {
          enquiry_id: enquiry.id,
          service_description: description.trim(),
          minutes: m,
          rate_per_minute: r,
        },
        { withCredentials: true, timeout: 15000 }
      );
      setInvoices((list) => [res.data, ...list]);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      resetForm();
    } catch (err) {
      console.error("Create invoice failed:", err);
      const detail = err?.response?.data?.detail || "Couldn't create invoice.";
      toast.error(typeof detail === "string" ? detail : "Couldn't create invoice.");
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 20000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download PDF failed:", err);
      toast.error("Couldn't download PDF.");
    } finally {
      setBusyId(null);
    }
  };

  const sendToCustomer = async (inv) => {
    if (!inv.customer_email) {
      toast.error("No customer email on file. Download & send manually.");
      return;
    }
    setComposeInv(inv);
  };

  const markPaid = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await axios.patch(
        `${API}/invoices/${inv.id}`,
        { status: "paid" },
        { withCredentials: true, timeout: 10000 }
      );
      setInvoices((list) => list.map((x) => (x.id === inv.id ? res.data : x)));
      toast.success("Marked as paid");
    } catch (err) {
      console.error("Mark paid failed:", err);
      toast.error("Couldn't update invoice.");
    } finally {
      setBusyId(null);
    }
  };

  const patchInvoice = async (inv, patch) => {
    const res = await axios.patch(
      `${API}/invoices/${inv.id}`,
      patch,
      { withCredentials: true, timeout: 12000 }
    );
    setInvoices((list) => list.map((x) => (x.id === inv.id ? res.data : x)));
    return res.data;
  };

  return (
    <div className="mt-5 pt-5 border-t border-slate-200" data-testid={`invoices-panel-${enquiry.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
          <FileText className="w-4 h-4" />
          Invoicing
          {invoices.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold">
              {invoices.length}
            </span>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            data-testid={`invoice-create-toggle-${enquiry.id}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
            Create invoice
          </button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          data-testid={`invoice-form-${enquiry.id}`}
          className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">New invoice</h4>
            <button
              type="button"
              onClick={resetForm}
              data-testid={`invoice-form-close-${enquiry.id}`}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Service description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid={`invoice-description-${enquiry.id}`}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                placeholder="e.g. Home visit — laptop setup and email fix"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Minutes worked
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                data-testid={`invoice-minutes-${enquiry.id}`}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                placeholder="e.g. 45"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Rate ($/min){" "}
                <span className="text-slate-400 font-normal">
                  (default {defaultRateLabelFor(enquiry)})
                </span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid={`invoice-rate-${enquiry.id}`}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-slate-700">
              Total:{" "}
              <span
                data-testid={`invoice-total-preview-${enquiry.id}`}
                className="text-lg font-bold text-slate-900"
              >
                ${total}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid={`invoice-submit-${enquiry.id}`}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Create invoice
              </button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-3 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading invoices…
        </div>
      ) : invoices.length === 0 && !showForm ? (
        <p className="text-sm text-slate-500 py-1">
          No invoices yet for this enquiry.
        </p>
      ) : (
        <ul className="space-y-2" data-testid={`invoices-list-${enquiry.id}`}>
          {invoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              inv={inv}
              busy={busyId === inv.id}
              presets={presets}
              onDownload={() => downloadPdf(inv)}
              onSend={() => sendToCustomer(inv)}
              onMarkPaid={() => markPaid(inv)}
              onPatch={(patch) => patchInvoice(inv, patch)}
            />
          ))}
        </ul>
      )}

      <ComposeEmailModal
        inv={composeInv}
        onClose={() => setComposeInv(null)}
        onSent={(updated) => {
          setInvoices((list) => list.map((x) => (x.id === updated.id ? updated : x)));
          setComposeInv(null);
        }}
      />
    </div>
  );
}

function InvoiceRow({ inv, busy, presets = [], onDownload, onSend, onMarkPaid, onPatch }) {
  const meta = INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.draft;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => withLineKeys(inv.extra_lines));
  const [saving, setSaving] = useState(false);

  // Reset local draft whenever the invoice's server-side lines change
  useEffect(() => {
    setDraft(withLineKeys(inv.extra_lines));
  }, [inv.extra_lines, inv.id]);

  const base = Number(inv.minutes) * Number(inv.rate_per_minute);
  const draftExtrasSum = draft.reduce(
    (acc, l) => acc + (Number(l.amount) || 0),
    0
  );
  const draftTotal = base + draftExtrasSum;

  const dirty = useMemo(() => {
    const a = inv.extra_lines || [];
    if (a.length !== draft.length) return true;
    return draft.some(
      (l, i) =>
        (l.description || "") !== (a[i]?.description || "") ||
        Number(l.amount) !== Number(a[i]?.amount)
    );
  }, [draft, inv.extra_lines]);

  const newDraftLine = (isDiscount) => ({
    description: isDiscount ? "Discount" : "",
    amount: isDiscount ? -0 : 0,
    _key: genLineKey(),
  });

  const addLine = (isDiscount) => {
    setDraft((d) => [...d, newDraftLine(isDiscount)]);
  };
  const updateLine = (idx, field, value) => {
    setDraft((d) =>
      d.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
    );
  };
  const removeLine = (idx) => {
    setDraft((d) => d.filter((_, i) => i !== idx));
  };

  const cancel = () => {
    setDraft(withLineKeys(inv.extra_lines));
    setEditing(false);
  };

  const save = async () => {
    // Validate: every line must have description; amount numeric
    for (const l of draft) {
      if (!String(l.description || "").trim()) {
        toast.error("Every line needs a description.");
        return;
      }
      if (!Number.isFinite(Number(l.amount))) {
        toast.error("Every line needs a valid amount.");
        return;
      }
    }
    setSaving(true);
    try {
      await onPatch({
        extra_lines: draft.map((l) => ({
          description: String(l.description).trim(),
          amount: Number(l.amount),
        })),
      });
      toast.success("Invoice updated");
      setEditing(false);
    } catch (err) {
      console.error("Save lines failed:", err);
      const detail = err?.response?.data?.detail || "Couldn't save changes.";
      toast.error(typeof detail === "string" ? detail : "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const hasExtras = (inv.extra_lines || []).length > 0;

  return (
    <li
      data-testid={`invoice-row-${inv.id}`}
      className="bg-white border border-slate-200 rounded-lg px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">
              {inv.invoice_number}
            </span>
            <span
              className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.className}`}
            >
              {meta.label}
            </span>
            <span
              data-testid={`invoice-total-${inv.id}`}
              className="text-sm font-semibold text-slate-900"
            >
              ${Number(inv.total).toFixed(2)}
            </span>
            {hasExtras && (
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                {inv.extra_lines.length} extra{inv.extra_lines.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-500 truncate max-w-md">
            {inv.minutes}min × ${Number(inv.rate_per_minute).toFixed(2)}/min · {inv.service_description}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setEditing((v) => !v)}
            data-testid={`invoice-edit-toggle-${inv.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-medium"
          >
            <Pencil className="w-3.5 h-3.5" />
            {editing ? "Hide lines" : "Edit lines"}
          </button>
          <button
            onClick={onDownload}
            disabled={busy}
            data-testid={`invoice-download-${inv.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-medium disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            PDF
          </button>
          <button
            onClick={onSend}
            disabled={busy || !inv.customer_email}
            title={!inv.customer_email ? "No customer email on file" : "Email PDF to customer"}
            data-testid={`invoice-send-${inv.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-xs font-semibold disabled:opacity-40"
          >
            <Send className="w-3.5 h-3.5" />
            Send
          </button>
          {inv.status !== "paid" && (
            <button
              onClick={onMarkPaid}
              disabled={busy}
              data-testid={`invoice-mark-paid-${inv.id}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"
            >
              <DollarSign className="w-3.5 h-3.5" />
              Mark paid
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div
          data-testid={`invoice-lines-editor-${inv.id}`}
          className="mt-4 pt-4 border-t border-dashed border-slate-200"
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
            Extra lines &amp; discounts
          </div>

          {/* Base line — read-only summary */}
          <div className="flex items-center justify-between text-sm text-slate-700 py-1.5">
            <span className="truncate mr-2">
              {inv.service_description}{" "}
              <span className="text-slate-400">
                — {inv.minutes}min × ${Number(inv.rate_per_minute).toFixed(2)}
              </span>
            </span>
            <span className="font-medium text-slate-900">${base.toFixed(2)}</span>
          </div>

          {draft.map((line, idx) => {
            const negative = Number(line.amount) < 0;
            return (
              <div
                key={line._key}
                data-testid={`invoice-line-row-${inv.id}-${idx}`}
                className="mt-2 grid grid-cols-12 gap-2 items-center"
              >
                <input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateLine(idx, "description", e.target.value)}
                  placeholder={negative ? "Discount reason" : "Extra item (e.g. USB cable)"}
                  data-testid={`invoice-line-desc-${inv.id}-${idx}`}
                  className="col-span-7 h-9 px-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                />
                <div className="col-span-4 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={line.amount}
                    onChange={(e) =>
                      updateLine(idx, "amount", e.target.value === "" ? 0 : parseFloat(e.target.value))
                    }
                    data-testid={`invoice-line-amount-${inv.id}-${idx}`}
                    className={`w-full h-9 pl-5 pr-2 rounded-lg border bg-white text-sm text-right focus:border-[#1E56B8] focus:ring-0 outline-none ${
                      negative
                        ? "border-emerald-300 text-emerald-700"
                        : "border-slate-300 text-slate-900"
                    }`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  data-testid={`invoice-line-remove-${inv.id}-${idx}`}
                  className="col-span-1 h-9 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  aria-label="Remove line"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => addLine(false)}
              data-testid={`invoice-add-line-${inv.id}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-medium"
            >
              <Plus className="w-3.5 h-3.5" />
              Add extra line
            </button>
            <button
              type="button"
              onClick={() => addLine(true)}
              data-testid={`invoice-add-discount-${inv.id}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-medium"
            >
              <Percent className="w-3.5 h-3.5" />
              Add discount
            </button>
          </div>

          {presets.length > 0 && (
            <div className="mt-3" data-testid={`invoice-presets-${inv.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Quick add
              </div>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p) => {
                  const isDiscount = Number(p.amount) < 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`invoice-preset-add-${inv.id}-${p.id}`}
                      onClick={() =>
                        setDraft((d) => [
                          ...d,
                          {
                            description: p.description,
                            amount: Number(p.amount),
                            _key: genLineKey(),
                          },
                        ])
                      }
                      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium border ${
                        isDiscount
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                          : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                      {p.description}
                      <span className={isDiscount ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                        {isDiscount ? "−" : "+"}${Math.abs(Number(p.amount)).toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5">
                Manage presets on the{" "}
                <Link to="/admin/invoices" className="underline hover:text-slate-600">
                  Invoices page
                </Link>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="mt-4 flex items-end justify-between border-t border-slate-200 pt-3">
            <div className="text-xs text-slate-500 leading-relaxed">
              <div>Base: ${base.toFixed(2)}</div>
              <div>
                Adjustments:{" "}
                <span
                  className={
                    draftExtrasSum < 0
                      ? "text-emerald-700 font-medium"
                      : "text-slate-700 font-medium"
                  }
                >
                  {draftExtrasSum < 0 ? "-" : ""}${Math.abs(draftExtrasSum).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                New total
              </div>
              <div
                data-testid={`invoice-lines-total-preview-${inv.id}`}
                className="text-xl font-bold text-slate-900"
              >
                ${draftTotal.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={cancel}
              className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid={`invoice-lines-save-${inv.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#1E56B8] hover:bg-[#2E6BD9] text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save changes
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
